import React from 'react';
import { X } from 'lucide-react';
import { MAINTENANCE_ITEMS } from '../kegops-utils';
import * as SupabaseService from '../supabaseService';

const CATEGORY_OPTIONS = [
  { key: 'keg', label: 'Keg Maintenance' },
  { key: 'line', label: 'Kegerator / Line Cleaning' },
  { key: 'water', label: 'Water Filtration (RO/DI)' }
];

const FILTER_TYPES = [
  { key: 'sediment', label: 'Sediment Filter' },
  { key: 'carbon', label: 'Carbon Block' },
  { key: 'ro', label: 'RO Membrane' },
  { key: 'di', label: 'DI' }
];

// Maintenance action types for water filtration
const WATER_MAINTENANCE_ACTIONS = [
  { key: 'inspection', label: 'Inspection', desc: 'Visual check, PPM measurement, or pressure test' },
  { key: 'cleaning', label: 'Cleaning', desc: 'Backwash, rinse, or sanitize filter' },
  { key: 'replacing', label: 'Replacing', desc: 'Replace filter cartridge or media' }
];

export const MassMaintenanceModal = ({ show, onClose, breweryId, kegs = [], locations = [], onSaved, initialCategory = null, initialFilterTypes = [], enabledFilterTypes = [] }) => {
  const [category, setCategory] = React.useState(initialCategory || 'keg');
  const [date, setDate] = React.useState(new Date().toISOString().split('T')[0]);
  const [notes, setNotes] = React.useState('');
  
  // Water filtration specific state
  const [selectedMaintenanceAction, setSelectedMaintenanceAction] = React.useState('inspection');
  const [resetBatchCounter, setResetBatchCounter] = React.useState(false);
  
  React.useEffect(() => {
    // When modal opens, initialize category and filters if provided
    if (show) {
      setCategory(initialCategory || 'keg');
      // Use enabledFilterTypes if provided (from brewery settings), otherwise fall back to initialFilterTypes
      const filtersToUse = enabledFilterTypes.length > 0 ? enabledFilterTypes : (initialFilterTypes || []);
      setSelectedFilterTypes(filtersToUse);
      setSelectedMaintenanceAction('inspection');
      setResetBatchCounter(false);
    }
  }, [show, initialCategory, initialFilterTypes, enabledFilterTypes]);

  // Filter types to display - only show enabled ones from brewery settings
  const displayableFilterTypes = React.useMemo(() => {
    if (enabledFilterTypes.length > 0) {
      return FILTER_TYPES.filter(f => enabledFilterTypes.includes(f.key));
    }
    return FILTER_TYPES; // Fall back to all if none specified
  }, [enabledFilterTypes]);

  // Keg selection
  const [selectedKegIds, setSelectedKegIds] = React.useState([]);
  const [selectAllKegs, setSelectAllKegs] = React.useState(false);

  // Keg maintenance items (exclude Line Cleaning & Water Filtration)
  const kegItems = MAINTENANCE_ITEMS.filter(i => i !== 'Line Cleaning' && i !== 'Water Filtration');
  const [selectedKegItems, setSelectedKegItems] = React.useState([]);

  // Line selection - build entries with taps and location/sublocation info
  const selectableLocations = React.useMemo(() => {
    const out = [];
    (locations || []).forEach(loc => {
      if (loc.sublocations && loc.sublocations.length > 0) {
        loc.sublocations.forEach(sub => {
          out.push({ displayName: `${loc.name} → ${sub.name}`, location: loc.name, sublocation: sub.name, tapNames: sub.tapNames || [] });
        });
      } else {
        out.push({ displayName: loc.name, location: loc.name, sublocation: '', tapNames: loc.tapNames || [] });
      }
    });
    return out;
  }, [locations]);

  const [selectedLines, setSelectedLines] = React.useState({}); // key -> { taps: [], all: bool }

  // Water filtration selections
  const [selectedFilterTypes, setSelectedFilterTypes] = React.useState([]);

  React.useEffect(() => {
    if (!show) {
      // reset
      setCategory('keg');
      setDate(new Date().toISOString().split('T')[0]);
      setNotes('');
      setSelectedKegIds([]);
      setSelectAllKegs(false);
      setSelectedKegItems([]);
      setSelectedLines({});
      setSelectedFilterTypes([]);
      setSelectedMaintenanceAction('inspection');
      setResetBatchCounter(false);
    }
  }, [show]);

  const toggleKeg = (id) => {
    setSelectedKegIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const handleSelectAllKegs = () => {
    if (selectAllKegs) {
      setSelectedKegIds([]);
      setSelectAllKegs(false);
    } else {
      setSelectedKegIds(kegs.map(k => k.id));
      setSelectAllKegs(true);
    }
  };

  const toggleKegItem = (it) => {
    setSelectedKegItems(prev => prev.includes(it) ? prev.filter(x => x !== it) : [...prev, it]);
  };

  const toggleLineTap = (locKey, tapName) => {
    setSelectedLines(prev => {
      const cur = prev[locKey] || { taps: [], all: false };
      const taps = cur.taps.includes(tapName) ? cur.taps.filter(t => t !== tapName) : [...cur.taps, tapName];
      return { ...prev, [locKey]: { ...cur, taps, all: taps.length > 0 && false } };
    });
  };

  const toggleLineAll = (locKey, tapNames = []) => {
    setSelectedLines(prev => {
      const cur = prev[locKey] || { taps: [], all: false };
      if (cur.all) {
        return { ...prev, [locKey]: { taps: [], all: false } };
      }
      return { ...prev, [locKey]: { taps: tapNames.slice(), all: true } };
    });
  };

  const toggleFilterType = (key) => {
    setSelectedFilterTypes(prev => prev.includes(key) ? prev.filter(x => x !== key) : [...prev, key]);
  };

  const handleSubmit = async () => {
    if (!breweryId) return alert('Please select a brewery');
    if (category === 'keg') {
      if (selectedKegItems.length === 0) return alert('Select at least one maintenance item');
      if (selectedKegIds.length === 0) return alert('Select at least one keg');

      const results = await Promise.all(selectedKegIds.map(id => 
        SupabaseService.addMaintenanceRecord(id, { date, items: selectedKegItems, notes })
      ));

      const failed = results.filter(r => !r || r.success === false);
      if (failed.length === 0) {
        alert('Maintenance recorded for selected kegs');
        onSaved?.();
        onClose();
      } else {
        alert('Some updates failed');
      }
    } else if (category === 'line') {
      // Build list of unique location/sublocation entries
      const entries = [];
      Object.keys(selectedLines).forEach(key => {
        const [location, sublocation] = key.split('||');
        const sel = selectedLines[key];
        if (sel && (sel.all || sel.taps.length > 0)) {
          const itemNotes = sel.all ? notes : `${notes} | taps: ${sel.taps.join(', ')}`;
          entries.push({ location, sublocation: sublocation || null, notes: itemNotes });
        }
      });

      if (entries.length === 0) return alert('Select at least one location/line');

      const results = await Promise.all(entries.map(e => {
        const tapNames = (e.taps && e.taps.length > 0) ? e.taps : null; // null => reset all taps
        return SupabaseService.recordLocationMaintenance(breweryId, e.location, e.sublocation, ['Line Cleaning'], e.notes, tapNames);
      }));

      const failed = results.filter(r => !r || r.success === false);
      if (failed.length === 0) {
        alert('Line cleaning recorded for selected locations');
        onSaved?.();
        onClose();
      } else {
        alert('Some updates failed');
      }
    } else if (category === 'water') {
      if (selectedFilterTypes.length === 0) return alert('Select at least one filter type');
      
      // Build items with action type and filter type
      const actionLabel = WATER_MAINTENANCE_ACTIONS.find(a => a.key === selectedMaintenanceAction)?.label || 'Maintenance';
      const items = selectedFilterTypes.map(f => {
        const filterLabel = FILTER_TYPES.find(ft => ft.key === f)?.label || f;
        return `${actionLabel}: ${filterLabel}`;
      });
      
      const result = await SupabaseService.recordBreweryMaintenance(breweryId, items, notes || `Water filtration ${actionLabel.toLowerCase()}`, resetBatchCounter);
      if (result.success) {
        alert(`Water filtration ${actionLabel.toLowerCase()} recorded${resetBatchCounter ? ' (batch counter reset)' : ''}`);
        onSaved?.();
        onClose();
      } else {
        alert('Failed to record water filtration maintenance: ' + result.error);
      }
    }
  };

  if (!show) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-3xl bg-white dark:bg-gray-800 rounded shadow p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="text-lg font-semibold">Add Maintenance Activity</div>
          <button onClick={onClose}><X /></button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          <div>
            <label className="block text-xs font-medium mb-1">Category</label>
            <select value={category} onChange={(e) => setCategory(e.target.value)} className="w-full border rounded px-2 py-1">
              {CATEGORY_OPTIONS.map(c => <option key={c.key} value={c.key}>{c.label}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium mb-1">Date</label>
            <input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="w-full border rounded px-2 py-1" />
          </div>
          <div>
            <label className="block text-xs font-medium mb-1">Notes</label>
            <input type="text" value={notes} onChange={(e) => setNotes(e.target.value)} className="w-full border rounded px-2 py-1" placeholder="Optional notes" />
          </div>
        </div>

        {category === 'keg' && (
          <div className="space-y-4">
            <div>
              <div className="flex items-center justify-between mb-2">
                <div className="text-sm font-medium">Keg Maintenance Items</div>
                <div className="text-xs text-gray-500">Select the maintenance tasks performed</div>
              </div>
              <div className="flex gap-2 flex-wrap">
                {kegItems.map(i => (
                  <label key={i} className="px-2 py-1 border rounded cursor-pointer flex items-center gap-2">
                    <input type="checkbox" checked={selectedKegItems.includes(i)} onChange={() => toggleKegItem(i)} />
                    <span className="text-sm">{i}</span>
                  </label>
                ))}
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <div className="text-sm font-medium">Select Kegs</div>
                <div className="text-xs text-gray-500">Selected {selectedKegIds.length}</div>
              </div>
              <div className="flex items-center gap-2 mb-2">
                <label className="flex items-center gap-2">
                  <input type="checkbox" checked={selectAllKegs} onChange={handleSelectAllKegs} />
                  <span className="text-sm">Select All</span>
                </label>
              </div>
              <div className="max-h-44 overflow-auto border rounded p-2 grid gap-2">
                {kegs.map(k => (
                  <label key={k.id} className="flex items-center justify-between p-2 border rounded">
                    <div>
                      <div className="text-sm font-medium">{k.name} <span className="text-xs text-gray-500">#{k.kegNumber}</span></div>
                      <div className="text-xs text-gray-500">{k.location}{k.sublocation ? ` → ${k.sublocation}` : ''}</div>
                    </div>
                    <input type="checkbox" checked={selectedKegIds.includes(k.id)} onChange={() => toggleKeg(k.id)} />
                  </label>
                ))}
              </div>
            </div>
          </div>
        )}

        {category === 'line' && (
          <div>
            <div className="text-sm font-medium mb-2">Select Locations / Lines</div>
            <div className="space-y-3 max-h-72 overflow-auto">
              {selectableLocations.map(loc => (
                <div key={loc.displayName} className="p-2 border rounded">
                  <div className="flex items-center justify-between mb-2">
                    <div className="font-medium">{loc.displayName}</div>
                    <div className="text-xs text-gray-500">{(loc.tapNames || []).length} taps</div>
                  </div>
                  <div className="flex gap-2 mb-2">
                    <label className="flex items-center gap-2">
                      <input type="checkbox" checked={(selectedLines[`${loc.location}||${loc.sublocation || ''}`]?.all) || false} onChange={() => toggleLineAll(`${loc.location}||${loc.sublocation || ''}`, loc.tapNames || [])} />
                      <span className="text-sm">Select All Taps</span>
                    </label>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    {(loc.tapNames || []).map(t => (
                      <label key={t} className="flex items-center gap-2 px-2 py-1 border rounded">
                        <input type="checkbox" checked={(selectedLines[`${loc.location}||${loc.sublocation || ''}`]?.taps || []).includes(t)} onChange={() => toggleLineTap(`${loc.location}||${loc.sublocation || ''}`, t)} />
                        <span className="text-sm">{t}</span>
                      </label>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {category === 'water' && (
          <div className="space-y-4">
            {/* Maintenance Action Type */}
            <div>
              <div className="text-sm font-medium mb-2">Maintenance Type</div>
              <div className="flex gap-2 flex-wrap">
                {WATER_MAINTENANCE_ACTIONS.map(a => (
                  <label key={a.key} className={`px-3 py-2 border rounded cursor-pointer ${selectedMaintenanceAction === a.key ? 'bg-amber-100 dark:bg-amber-900/30 border-amber-400' : 'bg-gray-50 dark:bg-gray-700'}`} title={a.desc}>
                    <input type="radio" name="maintenanceAction" checked={selectedMaintenanceAction === a.key} onChange={() => setSelectedMaintenanceAction(a.key)} className="sr-only" />
                    <div className="text-sm font-medium">{a.label}</div>
                    <div className="text-xs text-gray-500">{a.desc}</div>
                  </label>
                ))}
              </div>
            </div>

            {/* Filter Type Selection - only show enabled filters */}
            <div>
              <div className="text-sm font-medium mb-2">Select Filters</div>
              {displayableFilterTypes.length === 0 ? (
                <div className="text-sm text-gray-500 italic">No filter types configured. Configure them in Settings.</div>
              ) : (
                <div className="flex gap-2 flex-wrap">
                  {displayableFilterTypes.map(f => (
                    <label key={f.key} className={`px-2 py-1 border rounded flex items-center gap-2 cursor-pointer ${selectedFilterTypes.includes(f.key) ? 'bg-amber-100 dark:bg-amber-900/30 border-amber-400' : ''}`}>
                      <input type="checkbox" checked={selectedFilterTypes.includes(f.key)} onChange={() => toggleFilterType(f.key)} />
                      <div className="text-sm">{f.label}</div>
                    </label>
                  ))}
                </div>
              )}
            </div>

            {/* Reset Batch Counter Option */}
            <div className="p-3 border rounded bg-gray-50 dark:bg-gray-700">
              <label className="flex items-center gap-3 cursor-pointer">
                <input 
                  type="checkbox" 
                  checked={resetBatchCounter} 
                  onChange={(e) => setResetBatchCounter(e.target.checked)}
                  className="w-5 h-5"
                />
                <div>
                  <div className="text-sm font-medium">Reset Batch Counter</div>
                  <div className="text-xs text-gray-500">Set the batch count back to 0 after this maintenance (typically after replacing filters)</div>
                </div>
              </label>
            </div>
          </div>
        )}

        <div className="mt-6 flex items-center justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 bg-gray-200 rounded">Cancel</button>
          <button onClick={handleSubmit} className="px-4 py-2 bg-amber-600 text-white rounded">Save</button>
        </div>

      </div>
    </div>
  );
};
